import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Truck, Sparkles, Wrench, Settings, ArrowRight } from "lucide-react";
import movingService from "@/assets/moving-service-optimized.webp";
import cleaningService from "@/assets/cleaning-service-small.webp";
import plumbingService from "@/assets/plumbing-service-small.webp";
import renovationService from "@/assets/renovation-painting.webp";
import gardenService from "@/assets/garden-maintenance.webp";
import handymanService from "@/assets/handyman-assembly.webp";
import deliveryService from "@/assets/delivery-van.webp";
import domesticHelpService from "@/assets/domestic-help.webp";
import { useLanguage } from "@/contexts/LanguageContext";
import hvacPlumbingImg from "@/assets/hvac-plumbing-service.webp";

const Services = () => {
  const { t, language } = useLanguage();
  
  const services = [
    {
      icon: Wrench,
      title: { fi: "LVI & Viemäripalvelut / HVAC & Plumbing", en: "HVAC & Plumbing Services" },
      description: { fi: "Viemäriputkien asennus ja korjaus, viemärien tukkeutumien avaus, WC-laitteiden huolto ja korjaus, ilmanvaihtosuodattimien vaihto. Min 2h.", en: "Drain pipe installation and repair, drain unclogging and cleaning, toilet maintenance and repair, ventilation filter replacement. Min 2 hrs." },
      features: { 
        fi: ["Viemärien asennus ja korjaus", "Viemärien tukkeutumien avaus", "WC-laitteiden huolto ja korjaus", "Ilmanvaihtosuodattimen vaihto"],
        en: ["Drain installation & repair", "Drain unclogging & cleaning", "Toilet maintenance & repair", "Ventilation filter replacement"]
      },
      image: hvacPlumbingImg,
      price: "54€/tunti",
      priceEn: "54€/hour",
      minHours: 2
    },
    {
      icon: Wrench,
      title: { fi: "Putkiasennukset / Plumbing", en: "Plumbing Services" },
      description: { fi: "Hanojen vaihto, pesu- ja astianpesukoneiden asennus, viemärien puhdistus. Min 1.5h.", en: "Tap replacement, washing machine/dishwasher installation, sink pipe cleaning. Min 1.5 hrs." },
      features: { 
        fi: ["Hanojen vaihto", "Pesukoneen asennus", "Astianpesukoneen asennus", "Viemärien puhdistus"],
        en: ["Tap replacement", "Washing machine install", "Dishwasher install", "Pipe cleaning"]
      },
      image: plumbingService,
      price: "54€/tunti",
      priceEn: "54€/hour",
      minHours: 1.5,
      popular: true
    },
    {
      icon: Sparkles,
      title: { fi: "Siivouspalvelut / Cleaning", en: "Cleaning Services" },
      description: { fi: "Koti-, syvä-, ikkuna-, toimisto- ja remonttisiivous. Min 2h.", en: "Home, deep, window, office, and renovation cleaning. Min 2 hrs." },
      features: { 
        fi: ["Kotisiivous", "Syvä siivous", "Ikkunoiden pesu", "Toimistosiivous"],
        en: ["Home cleaning", "Deep cleaning", "Window washing", "Office cleaning"]
      },
      image: cleaningService,
      price: "42€/tunti",
      priceEn: "42€/hour",
      minHours: 2
    },
    {
      icon: Settings,
      title: { fi: "Remontointi / Renovation", en: "Renovation Services" },
      description: { fi: "Maalaus, tapetit, remontointi. Ammattitaitoista jälkeä. Min 2h.", en: "Painting, wallpapering, remodeling. Professional results. Min 2 hrs." },
      features: { 
        fi: ["Maalaustyöt", "Tapettien asennus", "Remontointi", "Sisustus"],
        en: ["Painting work", "Wallpaper install", "Remodeling", "Interior design"]
      },
      image: renovationService,
      price: "42€/tunti",
      priceEn: "42€/hour",
      minHours: 2
    },
    {
      icon: Wrench,
      title: { fi: "Puutarha- ja kotihoito / Garden & Home Maintenance", en: "Garden & Home Maintenance" },
      description: { fi: "Nurmikon leikkuu, puiden karsinta, puutarhan siivous ja hoito. Min 4h.", en: "Grass cutting, tree trimming, garden cleaning and maintenance. Min 4 hrs." },
      features: { 
        fi: ["Nurmikon leikkuu", "Puiden karsinta", "Puutarhan siivous", "Puutarhan hoito"],
        en: ["Grass cutting", "Tree trimming", "Garden cleaning", "Garden maintenance"]
      },
      image: gardenService,
      price: "42€/tunti",
      priceEn: "42€/hour",
      minHours: 4
    },
    {
      icon: Settings,
      title: { fi: "Käsityömies / Handyman", en: "Handyman Services" },
      description: { fi: "IKEA-kaappien korjaus, seinien korjaus, sänkyjen/sohvien asennus. Min 2h.", en: "Fixing IKEA cabinets, wall repairs, beds/sofas assembly. Min 2 hrs." },
      features: { 
        fi: ["IKEA korjaukset", "Seinien korjaus", "Huonekalujen asennus", "Yleiset korjaukset"],
        en: ["IKEA repairs", "Wall repairs", "Furniture assembly", "General repairs"]
      },
      image: handymanService,
      price: "39€/tunti",
      priceEn: "39€/hour",
      minHours: 2
    },
    {
      icon: Truck,
      title: { fi: "Muuttopalvelut / Moving", en: "Moving Services" },
      description: { fi: "Paikalliset ja pitkän matkan muutot. Turvallista kuljetusta.", en: "Local and long-distance moves. Safe transportation." },
      features: { 
        fi: ["Paikalliset muutot", "Pitkän matkan muutot", "Pakkauspalvelu", "Purkupalvelu"],
        en: ["Local moves", "Long-distance moves", "Packing service", "Unpacking service"]
      },
      image: movingService,
      price: "54€/tunti",
      priceEn: "54€/hour",
      minHours: 1
    },
    {
      icon: Truck,
      title: { fi: "Kuljetuspalvelut / Delivery", en: "Delivery Services" },
      description: { fi: "Kaupunki- ja kaupungin ulkopuoliset toimitukset. Min 2h.", en: "City and outside city deliveries. Min 2 hrs." },
      features: { 
        fi: ["Kaupunkikuljetukset", "Pitkät kuljetukset", "Tavaroiden nouto", "Toimitus kotiin"],
        en: ["City delivery", "Long delivery", "Item pickup", "Home delivery"]
      },
      image: deliveryService,
      price: "60€/tunti",
      priceEn: "60€/hour",
      minHours: 2
    },
    {
      icon: Sparkles,
      title: { fi: "Kotiapupalvelut / Domestic Help", en: "Domestic Help" },
      description: { fi: "Kantaminen, pakkaaminen, järjestäminen, lumen siivous. Min 1.5h.", en: "Carrying, packing, organizing, snow cleaning. Min 1.5 hrs." },
      features: { 
        fi: ["Kantaminen", "Pakkaaminen", "Järjestäminen", "Lumen siivous"],
        en: ["Carrying", "Packing", "Organizing", "Snow cleaning"]
      },
      image: domesticHelpService,
      price: "38€/tunti",
      priceEn: "38€/hour",
      minHours: 1.5
    }
  ];

  return (
    <section id="services" className="py-20 bg-gradient-subtle">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
            {t("services.title")}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {language === "en" 
              ? "From moves to cleaning, repairs to installations - we are your reliable partner for all home and office services in Helsinki."
              : "Muutosta siivoukseen, korjauksista asennuksiin - olemme luotettava kumppanisi kaikissa koti- ja toimistopalveluissa Helsingissä."
            }
          </p>
          <div className="mt-6">
            <a 
              href={`https://wa.me/358402401909?text=${encodeURIComponent(t(language === "en" ? "whatsapp.membership" : "whatsapp.membership.fi"))}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap"
            >
              🎯 {language === "en" ? "Membership 15€/2 years" : "Jäsenyys 15€/2 vuotta"}
            </a>
          </div>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {services.map((service, index) => (
            <Card key={index} className="group hover:shadow-xl transition-smooth bg-card border-0 shadow-md overflow-hidden">
              <div className="relative">
                <img 
                  src={service.image} 
                  alt={service.title[language]}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-smooth"
                  width="640"
                  height="192"
                  loading="lazy"
                />
                {service.popular && (
                  <div className="absolute top-4 left-4 bg-accent text-accent-foreground px-3 py-1 rounded-full text-sm font-semibold">
                    {language === "en" ? "Most Popular" : "Suosituin"}
                  </div>
                )}
                <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-semibold">
                  {language === "en" ? service.priceEn : service.price}
                </div>
              </div>
              
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gradient-service rounded-lg">
                    <service.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground">{service.title[language]}</h3>
                </div>
                
                <p className="text-muted-foreground mb-2">
                  {service.description[language]}
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  Min {service.minHours}h - {language === "en" ? service.priceEn : service.price}
                </p>
                
                <div className="grid grid-cols-2 gap-2 mb-6">
                  {service.features[language].map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="flex gap-3">
                  <Button 
                    variant="service" 
                    className="flex-1 group whitespace-nowrap" 
                    onClick={() => {
                      const serviceLinks = {
                        0: '/services/hvac',
                        1: '/services/plumbing',
                        2: '/services/cleaning', 
                        3: '/services/renovation',
                        4: '/services/garden',
                        5: '/services/handyman',
                        6: '/services/moving'
                      };
                      window.location.href = serviceLinks[index] || '#contact';
                    }}
                  >
                    {language === "en" ? "Learn more" : "Katso lisää"}
                    <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  <a 
                    href={`https://wa.me/358402401909?text=${encodeURIComponent(
                      t(language === "en" 
                        ? ["whatsapp.hvac", "whatsapp.plumbing", "whatsapp.cleaning", "whatsapp.renovation", "whatsapp.garden", "whatsapp.handyman", "whatsapp.moving", "whatsapp.delivery", "whatsapp.domestic"][index]
                        : ["whatsapp.hvac.fi", "whatsapp.plumbing.fi", "whatsapp.cleaning.fi", "whatsapp.renovation.fi", "whatsapp.garden.fi", "whatsapp.handyman.fi", "whatsapp.moving.fi", "whatsapp.delivery.fi", "whatsapp.domestic.fi"][index]
                      )
                    )}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors whitespace-nowrap"
                  >
                    WhatsApp
                  </a>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Call to action */}
        <div className="text-center bg-card rounded-2xl p-8 shadow-lg">
          <h3 className="text-2xl font-bold mb-4">
            {language === "en" ? "Need multiple services?" : "Tarvitsetko useita palveluita?"}
          </h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            {language === "en" 
              ? "Combine our services at a better price. Members get 15% off all services!"
              : "Yhdistä palveluitamme paremmalla hinnalla. Jäsenet saavat 15% alennusta kaikista palveluista!"
            }
          </p>
          <div className="space-y-4">
            <div className="bg-accent/10 p-4 rounded-lg">
              <h4 className="font-semibold mb-2">
                🎯 {language === "en" ? "Membership Benefits:" : "Jäsenyysedut:"}
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                {language === "en" ? (
                  <>
                    <li>• Surprise birthday gift</li>
                    <li>• 15% discount on all services</li>
                    <li>• Friendly customer service</li>
                    <li>• Automatic service reminders</li>
                    <li>• Priority service</li>
                  </>
                ) : (
                  <>
                    <li>• Yllätyssynttärilahja</li>
                    <li>• 15% alennus kaikista palveluista</li>
                    <li>• Ystävällinen asiakaspalvelu</li>
                    <li>• Automaattiset palvelumuistutukset</li>
                    <li>• Etuoikeutettu palvelu</li>
                  </>
                )}
              </ul>
            </div>
            <Button variant="hero" size="lg" onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })} className="whitespace-nowrap">
              {language === "en" ? "Contact Us" : "Ota yhteyttä"}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
